import type { CSSProperties } from 'react'

export const PRIMOLAB_BRAND_COLOR = '#FF6B35'
const COMPANY_BRAND_SOFT_ALPHA = 0.1
const NON_TEXT_CONTRAST_TARGET = 3

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/

export function normalizeHexColor(value: string): string | null {
  const normalized = value.trim().toUpperCase()
  return HEX_COLOR_PATTERN.test(normalized) ? normalized : null
}

export function hexToRgba(hexColor: string, alpha: number): string {
  const normalized = normalizeHexColor(hexColor) ?? PRIMOLAB_BRAND_COLOR
  const red = Number.parseInt(normalized.slice(1, 3), 16)
  const green = Number.parseInt(normalized.slice(3, 5), 16)
  const blue = Number.parseInt(normalized.slice(5, 7), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function relativeLuminance(hexColor: string): number {
  const normalized = normalizeHexColor(hexColor) ?? PRIMOLAB_BRAND_COLOR
  const channels = [1, 3, 5].map((offset) => {
    const channel = Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

function contrastRatio(first: string, second: string): number {
  const firstLuminance = relativeLuminance(first)
  const secondLuminance = relativeLuminance(second)
  return (
    (Math.max(firstLuminance, secondLuminance) + 0.05)
    / (Math.min(firstLuminance, secondLuminance) + 0.05)
  )
}

function mixHexColor(foreground: string, background: string, foregroundAlpha: number): string {
  const foregroundHex = normalizeHexColor(foreground) ?? PRIMOLAB_BRAND_COLOR
  const backgroundHex = normalizeHexColor(background) ?? '#FFFFFF'
  const channel = (offset: number) => {
    const foregroundValue = Number.parseInt(foregroundHex.slice(offset, offset + 2), 16)
    const backgroundValue = Number.parseInt(backgroundHex.slice(offset, offset + 2), 16)
    return Math.round(
      foregroundValue * foregroundAlpha + backgroundValue * (1 - foregroundAlpha)
    )
  }
  return `#${[1, 3, 5]
    .map((offset) => channel(offset).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase()
}

/**
 * Keeps the saved clinic color intact while deriving a marker that remains
 * visible against both the white header and the clinic-tinted navigation row.
 */
export function getCompanyBrandMarkerColor(hexColor: string): string {
  const normalized = normalizeHexColor(hexColor) ?? PRIMOLAB_BRAND_COLOR
  const softBackground = mixHexColor(normalized, '#FFFFFF', COMPANY_BRAND_SOFT_ALPHA)

  for (let darkeningStep = 0; darkeningStep <= 100; darkeningStep += 1) {
    const candidate = mixHexColor(normalized, '#000000', 1 - darkeningStep / 100)
    if (
      contrastRatio(candidate, '#FFFFFF') >= NON_TEXT_CONTRAST_TARGET
      && contrastRatio(candidate, softBackground) >= NON_TEXT_CONTRAST_TARGET
    ) {
      return candidate
    }
  }

  return '#000000'
}

export function getReadableForeground(hexColor: string): '#000000' | '#FFFFFF' {
  const luminance = relativeLuminance(hexColor)
  const contrastWithBlack = (luminance + 0.05) / 0.05
  const contrastWithWhite = 1.05 / (luminance + 0.05)
  return contrastWithBlack >= contrastWithWhite ? '#000000' : '#FFFFFF'
}

export function getCompanyBrandStyle(
  brandColor?: string | null,
  brandForeground?: string | null
): CSSProperties {
  const color = normalizeHexColor(brandColor ?? '') ?? PRIMOLAB_BRAND_COLOR
  const foreground = brandForeground === '#000000' || brandForeground === '#FFFFFF'
    ? brandForeground
    : getReadableForeground(color)
  return {
    '--company-brand': color,
    '--company-brand-soft': hexToRgba(color, COMPANY_BRAND_SOFT_ALPHA),
    '--company-brand-marker': getCompanyBrandMarkerColor(color),
    '--company-brand-foreground': foreground,
  } as CSSProperties
}
